import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ALERT_OPS, alertLine, failureText } from "../lib/reports.js";

/**
 * Being told when a figure crosses a line.
 *
 * What is said here is what the kernel does. Each person on the list has their *own*
 * figure compared with the line, counted from the records they can open, so it can cross
 * for one and not for another and nobody is told on account of a record they cannot see.
 * They are emailed once when it crosses, and again only after it has come back and
 * crossed again. They must already be able to open the report. It watches one figure, so
 * it is for a report that is not grouped.
 *
 * One alert on a report here. The kernel allows three, and editing this one keeps its
 * history: moving the line does not make the person look new, so a figure already over the
 * new line is not announced a second time.
 */
export default function AlertDialog({ item, me, label, onClose, onSave }) {
  const existing = item?.alerts?.[0] || null;
  const [op, setOp] = useState(existing?.op || "gt");
  const [threshold, setThreshold] = useState(existing ? String(existing.threshold) : "");
  const [people, setPeople] = useState(existing?.recipients || (me ? [me] : []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const go = async (alerts) => {
    setBusy(true);
    setError("");
    try {
      await onSave(item, alerts);
      onClose();
    } catch (e) {
      setError(failureText(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const n = Number(threshold);
    if (!threshold.trim() || !Number.isFinite(n)) {
      setError("The line is a number, such as 100 or 2500.50.");
      return;
    }
    go([{ ...(existing ? { id: existing.id } : {}), op, threshold: n, recipients: people }]);
  };

  return (
    <Dialog open={Boolean(item)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="alert-title">
      <DialogTitle id="alert-title" sx={{ overflowWrap: "anywhere" }}>Tell people about “{item?.name}”</DialogTitle>
      <DialogContent dividers>
        <Stack sx={{ gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Each person is told when <em>their own</em> figure crosses the line, counted from the records they can
            open. It is emailed once when it crosses, and again only after it has come back and crossed again. They
            must already be able to open the report, so share it with them first.
          </Typography>
          {existing ? <Typography variant="body2" role="status">Now: {alertLine(existing, label)}.</Typography> : null}
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
            <TextField select size="small" label={`When ${label} is`} value={op} onChange={(e) => setOp(e.target.value)} sx={{ minWidth: 240 }}>
              {Object.entries(ALERT_OPS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <TextField
              size="small" label="This number" value={threshold} onChange={(e) => setThreshold(e.target.value)}
              helperText="Money is in its own unit, so 5000 is 5,000.00." sx={{ minWidth: 200 }}
            />
          </Stack>
          <Autocomplete
            multiple freeSolo options={me ? [me] : []} value={people}
            onChange={(_, v) => setPeople(v.map((x) => String(x).trim()).filter(Boolean))}
            renderValue={(values, getTagProps) => values.map((value, index) => {
              const { key, ...props } = getTagProps({ index });
              return <Chip key={key} size="small" label={value} {...props} />;
            })}
            renderInput={(params) => (
              <TextField {...params} label="Tell" size="small"
                helperText="Their sign-in names. Press Enter after each; at most 20." />
            )}
          />
          {error ? <Alert severity="warning" role="alert">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {existing ? <Button color="inherit" onClick={() => go([])} disabled={busy}>Stop telling them</Button> : null}
        <Button variant="contained" onClick={save} disabled={busy || !people.length}>
          {busy ? "Saving…" : "Save alert"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
